import { useEffect, useState } from "react";
import {
  isChunkLoadError,
  reloadForStaleChunk,
  reloadPending,
} from "@/lib/chunk-reload";

// Top-level error-boundary fallback (mounted in main.tsx). Two distinct
// outcomes:
//
//  1. A stale-chunk 404 that surfaced as a *render-time throw* — a
//     nested chunk `lazyWithReload` doesn't wrap (Campaign Studio's
//     NameTemplateModal / CsvUploadPopup, the Rules "Apply To" popup,
//     a further-split vendor chunk). We DON'T show "Something broke" —
//     that's just a deploy catching the tab mid-flight. Reload silently
//     (self-expiring guard) and render a quiet "Updating…" placeholder
//     for the ~1s the reload takes. This killed the recurring "shows an
//     error, then reloads" report (CLAUDE.md §56.3).
//  2. A genuine crash → the honest "Something broke" panel with a
//     manual reload button.
export function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  const chunkError = isChunkLoadError(error);
  // A chunk error whose reload was refused AND with no reload on the way
  // means we reloaded moments ago and the chunk STILL fails — mid-deploy
  // or a genuinely broken build. "Updating…" forever would hide that;
  // fall through to the honest panel with its Reload button.
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    if (chunkError && !reloadForStaleChunk() && !reloadPending()) {
      setStuck(true);
    }
  }, [chunkError]);

  if (chunkError && !stuck) {
    return (
      <div
        style={{
          padding: 32,
          fontFamily: "system-ui",
          color: "#888",
          margin: "80px auto",
          maxWidth: 540,
          textAlign: "center",
        }}
      >
        Updating to the latest version…
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 32,
        fontFamily: "system-ui",
        maxWidth: 540,
        margin: "80px auto",
      }}
    >
      <h1 style={{ fontSize: 22, marginBottom: 12 }}>Something broke</h1>
      <p style={{ color: "#555", lineHeight: 1.5, marginBottom: 18 }}>
        The page hit an unexpected error. We&apos;ve been notified and will
        investigate. Reloading usually clears it — your data isn&apos;t
        affected.
      </p>
      <p style={{ fontFamily: "monospace", fontSize: 12, color: "#888" }}>
        {String((error as Error)?.message ?? error)}
      </p>
      <div style={{ marginTop: 24 }}>
        <button
          onClick={() => {
            resetError();
            window.location.reload();
          }}
          style={{
            padding: "8px 16px",
            background: "#0e1220",
            color: "#fff",
            border: 0,
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
